// frontend/src/App.js (Updated with Routing)
import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';

// Import components
import Dashboard from './components/Dashboard';
import NaturalLanguageSearch from './components/NaturalLanguageSearch';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Footer from './components/Footer';
import ReportsPage from './components/ReportsPage';
import AnalyticsPage from './components/AnalyticsPage';

// Set the base URL for all axios requests
const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5001';
axios.defaults.baseURL = API_URL;

function App() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [recentQueries, setRecentQueries] = useState([]);
  const [sampleQueries] = useState([
    "Which content has the highest viewership across all platforms?",
    "What is the demographic breakdown of viewers for drama series on Samsung TV Plus?",
    "Which regions experience the most buffering issues, and on which platforms?",
    "What's the conversion rate from viewing to app installs for our top 5 shows?",
    "How does the viewership of comedy content compare across different age groups?",
    "Which content types have the highest average completion rates?"
  ]);

  // Handle search query
  const handleSearch = async (query) => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post('/api/nlp/query', { query });
      setResult(response.data);
      
      // Add to recent queries if not already there
      if (!recentQueries.includes(query)) {
        const updatedQueries = [query, ...recentQueries.slice(0, 4)];
        setRecentQueries(updatedQueries);
      }
    } catch (err) {
      console.error('Error processing query:', err);
      setError({
        message: 'Failed to process your query. Please try again.',
        details: err.response?.data?.message || err.message
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle sample query click
  const handleSampleQueryClick = (query) => {
    handleSearch(query);
  };

  // Error component
  const ErrorMessage = ({ error }) => (
    <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md mb-6">
      <div className="flex">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-red-800">Error Processing Query</h3>
          <div className="mt-2 text-sm text-red-700">
            <p>{error.message}</p>
            {error.details && (
              <p className="mt-1 text-xs">{error.details}</p>
            )}
          </div>
          <div className="mt-3">
            <button
              type="button"
              onClick={() => setError(null)}
              className="text-sm font-medium text-red-700 hover:text-red-600"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Empty state component
  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center mt-10 py-12 bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="w-24 h-24 mb-6 bg-indigo-100 rounded-full flex items-center justify-center">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-gray-800 text-center">Welcome to ZEE FAST Analytics</h2>
      <p className="text-gray-500 mt-2 text-center max-w-md">
        Explore your FAST TV data using natural language queries. Ask questions about viewership, 
        demographics, content performance, quality of experience, and conversion metrics.
      </p>
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg">
        <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-100">
          <h3 className="font-medium text-indigo-800 text-sm">Ask about viewership</h3>
          <p className="text-indigo-600 text-xs mt-1">Who's watching what content and for how long?</p>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
          <h3 className="font-medium text-purple-800 text-sm">Demographics analysis</h3>
          <p className="text-purple-600 text-xs mt-1">Age, gender, and regional breakdown</p>
        </div>
        <div className="bg-green-50 p-4 rounded-lg border border-green-100">
          <h3 className="font-medium text-green-800 text-sm">Content performance</h3>
          <p className="text-green-600 text-xs mt-1">Ratings, completion rates, retention</p>
        </div>
        <div className="bg-amber-50 p-4 rounded-lg border border-amber-100">
          <h3 className="font-medium text-amber-800 text-sm">Conversion insights</h3>
          <p className="text-amber-600 text-xs mt-1">Journey from viewing to subscription</p>
        </div>
      </div>
    </div>
  );

  // Dashboard Container component that includes search and results
  const DashboardContainer = () => (
    <>
      <NaturalLanguageSearch onSearch={handleSearch} loading={loading} recentQueries={recentQueries} />
      {error && <ErrorMessage error={error} />}
      {result && <Dashboard result={result} loading={loading} />}
      {!result && !error && !loading && <EmptyState />}
    </>
  );

  return (
    <Router>
      <div className="flex flex-col min-h-screen bg-gray-50">
        <Header />

        <div className="flex flex-1 overflow-hidden">
          <Sidebar sampleQueries={sampleQueries} onSampleQueryClick={handleSampleQueryClick} />

          <main className="flex-1 p-6 overflow-y-auto">
            <Routes>
              <Route path="/" element={<DashboardContainer />} />
              <Route path="/dashboard" element={<Navigate to="/" replace />} />
              <Route path="/reports" element={<ReportsPage />} />
              <Route path="/analytics" element={<AnalyticsPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
        </div>
        
        <Footer />
      </div>
    </Router>
  );
}

export default App;
